import { Injectable } from '@angular/core';
import { LicenseArea } from 'src/app/data-models/area-models/license-area.model';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { Subarea } from 'src/app/data-models/area-models/subarea.model';
import { ProductType } from 'src/app/data-models/catch-table-models/product-type.model';
import { SpeciesData } from 'src/app/data-models/catch-table-models/species-data.model';
import { TableRules } from 'src/app/data-models/catch-table-models/table-rules.model';
import { LandingSite } from 'src/app/data-models/landing-site.model';
import { UserStorageService } from './user-storage.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CommonDataService {

  private static common_data: PouchDB.Database; // common data (read only)
  //For replicating from the common database, the credentials are an API key unique to the common database and can only be used to replicate data from Cloudant, but not write any data to it
  private static common_couch: string = environment.commonEndpoint;

  // global variables for common data
  public static licenseAreas: LicenseArea[] = [];
  public static managementAreas: ManagementArea[] = [];
  public static subareas: Subarea[] = [];
  public static netTypes: string[] = [];
  public static speciesList: SpeciesData[] = [];
  public static marineMammalInteractionSpeciesList: SpeciesData[] = [];
  public static landingSites: LandingSite[] = [];
  public static productTypes: ProductType[] = [];
  public static gillNetTableRules: TableRules = new TableRules();
  public static seineTableRules: TableRules = new TableRules();
  public static trollTableRules: TableRules = new TableRules();

  constructor() {

  }


  /*  ---------- Initialization Methods  ---------- */


  // open or create a pouch db for the user's common data
  private static initializeCommonDB(email: string): void {

    // a new common pouch db is created for each user (assuming userEmail is unique)
    this.common_data = UserStorageService.getPouchDB('common-' + email);

  }


  /*  ---------- Data Retrieval Methods  ---------- */


  // gets all docs provided in the docIDs array and sets the global variables appropriately
  public async getSpecifiedDocs(email: string, docIDs: string[]): Promise<boolean> {
    email = UserStorageService.adminUser?.userEmail ?? UserStorageService.currUser.userEmail;
    CommonDataService.initializeCommonDB(email);
    
    for (let docID of docIDs) {
      let doc: any = await CommonDataService.common_data.get(docID);
      CommonDataService.setDocData(doc);
    }

    return true;

  }

  // pulls (not syncs) data from the common couch db into the user's common pouch db
  public static pullCommonData(): Promise<boolean> {

    let email: string = UserStorageService.adminUser?.userEmail ?? UserStorageService.currUser.userEmail;

    return new Promise<boolean>((res) => {
      this.initializeCommonDB(email); // open pouch db
      this.common_data.replicate.from(environment.commonEndpoint).on('complete', () => {
        console.log('Pulled common data from couch');
        res(true);
      }).on('error', (err: any) => {
        console.log('Common data retrieval failed: ' + err);
        res(false);
      });
    });

  }


  /*  ---------- Set Global Variables  ---------- */


  // calls the appropriate set method for the given doc
  public static setDocData(doc: any) {

    switch (doc._id) {
      case 'license_areas_list':
        this.setLicenseAreas(doc.license_areas);
        break;
      case 'net_types':
        this.setNetTypes(doc.nets);
        break;
      case 'table_rules':
        this.setTableRules(doc.rules);
        break;
      case 'management_areas_list':
        this.setManagementAreas(doc.management_areas);
        break;
      case 'subareas_list':
        this.setSubareas(doc.subareas);
        break;
      case 'species_list':
        this.setSpeciesList(doc.species);
        break;
      case 'marine_mammal_interaction_species_list':
        this.setMarineMammalInteractionSpeciesList(doc.species);
        break;
      case 'landing_sites':
        this.setLandingSites(doc.landing_sites);
        break;
      case 'product_types':
        this.setProductTypes(doc.product_types);
        break;
    }

  }

  // deserialize landing site data from the pouch DB document
  public static setLandingSites(sites: any[]): void {

    this.landingSites.length = 0; // clear any old data
    for (let site of sites) {
      this.landingSites.push(LandingSite.transformAnonymous(site));
    }
    
  }

  // deserialize subarea data from the pouch DB document
  public static setSubareas(subareas: any[]): void {

    this.subareas.length = 0; // clear any old data
    for (let area of subareas) {
      this.subareas.push(Subarea.transformAnonymous(area));
    }

  }

  // deserialize management area data from the pouch DB document
  public static setManagementAreas(managementAreas: any[]): void {

    this.managementAreas.length = 0; // clear any old data
    for (let area of managementAreas) {
      this.managementAreas.push(ManagementArea.transformAnonymous(area));
    }

  }

  // deserialize license area data from the pouch DB document
  public static setLicenseAreas(licenseAreas: any[]): void {

    this.licenseAreas.length = 0; // clear any old data
    for (let area of licenseAreas) {
      this.licenseAreas.push(LicenseArea.transformAnonymous(area));
    }

  }

  // deserialize and separate table rules data from the pouch DB document
  public static setTableRules(rules: any) {

    for (let rule of rules) {
      let newTable: TableRules = new TableRules();
      newTable.tableType = rule.tableType;
      newTable.gearName = rule.gearName;
      for (let fish of rule.containsSpecies) {
        newTable.containsSpecies.push(fish);
      }
      for (let fish of rule.excludesSpecies) {
        newTable.excludesSpecies.push(fish);
      }
      for (let fish of rule.releaseOnlySpecies) {
        newTable.releaseOnlySpecies.push(fish);
      }
      for (let pair of rule.catchCategoryMapping) {
        newTable.catchCategoryMappings.set(pair[0], pair[1]);
      }
      
      switch (newTable.gearName) {
        case 'Gill Net':
          this.gillNetTableRules = newTable;
          break;
        case 'Seine':
          this.seineTableRules = newTable;
          break;
        case 'Troll':
          this.trollTableRules = newTable;
          break;
      }
    }

  }
  
  // set the net types for gill net reports
  public static setNetTypes(nets: any): void {

    this.netTypes.length = 0; // clear any pre-existing data
    for (let net of nets) {
      this.netTypes.push(net);
    }

  }

  // deserialize species data from the couch species list and populate the global array
  public static setSpeciesList(speciesList: any): void {
    
    this.speciesList.length = 0; // clear any pre-existing data
    for (let species of speciesList) {
      this.speciesList.push(SpeciesData.transformAnonymous(species));
    }

  }

  // deserialize species data from the couch species list and populate the global array
  public static setMarineMammalInteractionSpeciesList(marineMammalInteractionSpeciesList: any): void {

    this.marineMammalInteractionSpeciesList.length = 0; // clear any pre-existing data
    for (let species of marineMammalInteractionSpeciesList) {
      this.marineMammalInteractionSpeciesList.push(SpeciesData.transformAnonymous(species));
    }

  }

  // deserialize product type data and populate the global array 
  public static setProductTypes(productTypes: any): void {

    this.productTypes.length = 0;
    for (let productType of productTypes) {
      this.productTypes.push(ProductType.transformAnonymous(productType));
    }
    
  }


  /*  ---------- Clear/Close Common Data  ---------- */


  // reset common db data in code
  private static clearCommonData(): void {

    this.licenseAreas = [];
    this.managementAreas = [];
    this.subareas = [];
    this.netTypes = [];
    this.speciesList = [];
    this.landingSites = [];
    this.productTypes = [];
    this.gillNetTableRules = new TableRules();
    this.seineTableRules = new TableRules();
    this.trollTableRules = new TableRules();

  }
  
  // close the user's common pouch db (invoked on logout)
  public static closeCommonDb(isAdmin: boolean): Promise<boolean> {

    this.clearCommonData(); // ensure all global variables are reset

    return new Promise<boolean>((res) => {
      if (isAdmin) {
        console.log('Destroying admin common')
        if (this.common_data) this.common_data.destroy();
        res(true);
      } 
      else if (!this.common_data) res(true);
      else {
        this.common_data.close().then(() => {
          console.log('Closed common db');
          res(true);
        }).catch((err: { name: string }) => {
          console.log('Could not close common db: ' + err);
          res(false);
        });
      }     
    });

  }

}
